import { asyncAction } from '@play-co/replicant';
import createActions from '../../config/actions/createActions';
import {
  match2TournamentRuleset as ruleset,
  maxTournamentsAgeDays,
  TournamentType,
  match2TournamentRuleset,
} from './match2-tournament.ruleset';
import { action } from '@play-co/replicant';
import * as getters from './match2-tournament.getters';
import * as modifiers from './match2-tournament.modifiers';
import { grantMatch2Rewards } from '../match2.modifiers';
import { duration } from 'src/replicant/utils/duration';
import {
  AnalyticsEvent,
  Feature,
  sendAnalyticsEvents,
} from 'src/replicant/analytics';
import { calcBracket } from './match2-tournament.utils';
import { getMatch2Level } from '../match2.getters';
import { NumberFilters, NumericDecayFunction } from '@play-co/replicant';
import { roundTimestampUpTo } from 'src/replicant/utils';
import { AvatarFrameId } from 'src/replicant/profile/frames';
import { getPlayerProfileAvatarFrame } from 'src/replicant/profile/profile.getters';

export type Match2ModelTournamentPlayer = {
  id: string;
  score: number;
  scoreUpdatedAt: number;
  avatar: string;
  frame: AvatarFrameId;
  name: string;
  startedAt: number;
};
export type Match2ModelTournamentData = {
  endDate: number;
  claimed: boolean;
  finalRank?: number;
  currentRank: number;
  player: Match2ModelTournamentPlayer;
  leaderboard: Array<Match2ModelTournamentPlayer>;
  matchMakingCriteria: string;
  type: TournamentType;
};

export const match2TournamentActions = createActions({
  /**
   * Run matchmaking for tournament event
   */
  runMatch2TournamentMatchmaking: asyncAction(
    async (state, { eventId }: { eventId: string }, api) => {
      const period = getters.getTournamentPeriodById(state, eventId);

      if (!period) {
        throw new Error(
          `[runMatch2TournamentMatchmaking]: Tournament is not defined`,
        );
      }
      const event = state.match2.tournament.events[period.eventId];

      if (!event) {
        throw new Error(
          `[runMatch2TournamentMatchmaking]: Player did not join tournament`,
        );
      }

      // Run matchmaking.

      let data: Match2ModelTournamentData;

      const now = api.date.now();
      const whereClauses: {
        match2Level?: NumberFilters;
        updatedAt?: NumberFilters;
      } = {
        updatedAt: {
          greaterThan: roundTimestampUpTo(now - duration({ days: 1 }), {
            minutes: 1,
          }),
        },
      };

      const scoringFunctions: {
        match2Level5Bracket?: NumericDecayFunction;
        match2TournamentLastStartedAt?: NumericDecayFunction;
        match2TournamentLastRank?: NumericDecayFunction;
        match2Booster5Bracket?: NumericDecayFunction;
        match2Powerup5Bracket?: NumericDecayFunction;
        gems50Bracket?: NumericDecayFunction;
      } = {};
      const scoringShould: {
        match2TournamentLastStartedAt?: NumberFilters;
        match2TournamentLastRank?: NumberFilters;
      } = {};
      let scoringMode: 'multiply' | 'sum';

      const flyingHighTournament = getters.isFlyingHighTournamentEnabled(
        state,
        now,
      );
      // matchmaking per tournament type test
      const periodV2 = getters.getTournamentPeriodForSchedule(
        match2TournamentRuleset.weeklyScheduleV2,
        new Date(eventId).getTime(),
      );

      if (periodV2 && flyingHighTournament) {
        whereClauses.match2Level = {
          greaterThanOrEqual: ruleset.flyingHighUnlockLevel,
        };
      } else {
        whereClauses.match2Level = {
          lessThan: ruleset.flyingHighUnlockLevel,
        };
      }

      const lastTournamentRank = state.match2?.tournament?.lastRank ?? 10; // default to 10 if first tournament
      const level5Bracket = calcBracket(5, getMatch2Level(state));

      // Prefer players who joined in the last 10 minutes
      scoringShould.match2TournamentLastStartedAt = {
        greaterThan: roundTimestampUpTo(
          api.date.now() - duration({ minutes: 10 }),
          { minutes: 1 },
        ),
      };
      // Prefer players who fit with the player tournament rank
      scoringShould.match2TournamentLastRank = {
        between: [
          1,
          Math.max(lastTournamentRank + Math.ceil(lastTournamentRank * 0.1), 3),
        ],
      };

      // Try to group players by level brackets
      scoringFunctions.match2Level5Bracket = {
        origin: level5Bracket,
        scale: Math.ceil(level5Bracket * 0.2) || 1,
        function: `gauss`,
      };
      // Try to group players by tournament start time
      scoringFunctions.match2TournamentLastStartedAt = {
        origin: api.date.now(),
        scale: duration({ minutes: 30 }),
        function: `gauss`,
      };
      // Try to group players by last +10% rank obtained
      scoringFunctions.match2TournamentLastRank = {
        origin: Math.max(
          lastTournamentRank - Math.ceil(lastTournamentRank * 0.1),
          2,
        ),
        offset: 1,
        scale: 5,
        function: `gauss`,
      };

      scoringMode = 'sum';

      // === SINGLE SCORE SORTED SEARCH ======================================================
      const matchedOpponents = await api.searchPlayers({
        where: {
          // not the player himself
          id: {
            isNotOneOf: [state.id],
          },
          // can score for current tournament event
          match2TournamentsEnabled: {
            is: true,
          },
          // additional matchmaking clauses
          ...whereClauses,
        },
        scoring: {
          functions: {
            ...scoringFunctions,
          },
          should: {
            ...scoringShould,
          },
          mode: scoringMode,
        },
        limit: ruleset.maxPlayers - 1, // -1 because we don't want to include the player itself
      });

      const opponnentsLookup = matchedOpponents.results;
      // -- Generate the leaderboard (unsorted)
      const leaderboard: Match2ModelTournamentPlayer[] = opponnentsLookup.map(
        (opponent) => {
          const tournament = opponent.match2Tournaments?.find(
            (event) => event.eventId === period.eventId,
          );
          const player = {
            id: opponent.id,
            score: tournament?.score || 0,
            scoreUpdatedAt: tournament?.scoreUpdatedAt || 0,
            name: opponent.publicProfileName,
            avatar: opponent.publicProfilePhoto,
            frame: opponent.profileAvatarFrame as AvatarFrameId,
            startedAt: tournament
              ? opponent.match2TournamentLastStartedAt
              : api.date.now(), // default to now if no tournament
          };
          return player;
        },
      );

      // generate the player object
      const player = {
        id: state.id,
        score: event?.player.score ?? 0,
        scoreUpdatedAt: event?.player.scoreUpdatedAt ?? 0,
        avatar: state.publicProfile?.photo || state.profile?.photo || '',
        frame: getPlayerProfileAvatarFrame(state),
        name: state.publicProfile?.name || state.profile?.name || '',
        startedAt: state.match2.tournament.lastStartedAt ?? api.date.now(), // default to now if no previous tournament
      };
      leaderboard.push(player);

      leaderboard.sort((a, b) => a.scoreUpdatedAt - b.scoreUpdatedAt);
      leaderboard.sort((a, b) => b.score - a.score);

      const currentRank =
        leaderboard.findIndex((player) => player.id === state.id) + 1;

      // Check if tournament has ended
      const ended = period.end < api.date.now();

      // If ended, set the finalRank
      const finalRank = ended ? currentRank : undefined;

      data = {
        endDate: period.end,
        claimed: !!event?.claimed,
        currentRank,
        finalRank: event?.finalRank ?? finalRank,
        player,
        leaderboard,
        matchMakingCriteria: 'scoredSearch',
        type: (event?.type as TournamentType) ?? 'star', // Fallback to star tournament if type is not defined - ie. on older tournament schema
      };

      // save in state
      event.opponentList = matchedOpponents.results.map((x) => x.id);
      event.matchMakingCriteria = 'scoredSearch';

      const oppAnalytics = {
        avgOppScore: 0,
        maxOppScore: 0,
        minOppScore: 0,
        avgOppTimeAdvantage: 0,
      };

      if (event.opponentList.length > 0) {
        const now = api.date.now();
        const { accOppScore, maxOppScore, minOppScore, accOppTimeAdvantage } =
          data.leaderboard
            .filter((x) => x.id !== state.id)
            .reduce(
              (prev, x) => ({
                accOppScore: prev.accOppScore + x.score,
                maxOppScore: Math.max(prev.maxOppScore, x.score),
                minOppScore: Math.min(prev.minOppScore, x.score),
                accOppTimeAdvantage:
                  prev.accOppTimeAdvantage + (now - x.startedAt) / 1000,
              }),
              {
                accOppScore: 0,
                maxOppScore: -Infinity,
                minOppScore: Infinity,
                accOppTimeAdvantage: 0,
              },
            );

        oppAnalytics.avgOppScore = accOppScore / event.opponentList.length;
        oppAnalytics.maxOppScore = maxOppScore;
        oppAnalytics.minOppScore = minOppScore;
        oppAnalytics.avgOppTimeAdvantage =
          Math.round((accOppTimeAdvantage / event.opponentList.length) * 100) /
          100; // in seconds, rounded to 2 decimals
      }

      sendAnalyticsEvents(api, [
        {
          eventType: 'StarTournamentJoined',
          eventProperties: {
            timeLeft: getters.getRemainingTime(state, api.date.now()),
            matchMakingCriteria: event.matchMakingCriteria!,
            totalMatched: event.opponentList?.length ?? 0,
            tournamentType: period.type,
            tournamentId: period.eventId,
            ...oppAnalytics,
          },
        },
      ]);

      return data;
    },
  ),

  /**
   * Update tournament event in player state
   */
  endMatch2TournamentEvent: asyncAction(
    async (state, { eventId }: { eventId: string }, api) => {
      const period = getters.getTournamentPeriodById(state, eventId);

      if (!period) {
        throw new Error(
          `[endMatch2TournamentEvent]: Tournament is not defined`,
        );
      }

      if (!state.match2?.tournament.events[period.eventId]) {
        throw new Error(
          `[endMatch2TournamentEvent]: Tournament does not exist`,
        );
      }

      // Fallback to star tournament if type is not defined - ie. on older tournament schema
      const type = getters.getStoredTournamentType(state, eventId);

      // Only check the end date for valid tournament
      if (type === period.type && api.date.now() < period.end) {
        throw new Error(
          `[endMatch2TournamentEvent]: Tournament has not ended yet`,
        );
      }

      const data = await api.asyncGetters.getMatch2TournamentEvent({ eventId });
      if (!data) {
        throw new Error(
          `[endMatch2TournamentEvent]: Could not gewt tournament data`,
        );
      }
      const { currentRank, matchMakingCriteria } = data;

      // Only end the tournament if it has not ended yet ie. finalRank is not set
      if (state.match2.tournament.events[period.eventId].finalRank) {
        return data;
      }

      // tournament is over, assign final rank and mark as ended
      state.match2.tournament.events[period.eventId].finalRank = currentRank;
      state.match2.tournament.lastRank = currentRank;
      state.match2.tournament.lastMatchMakingCriteria = matchMakingCriteria;
      if (currentRank <= 3) {
        state.match2.tournament.lastWinAt = api.date.now();
      }

      // For analytics only - might stay undefined if no opponents
      let prize;

      const oppAnalytics = {
        avgOppScore: 0,
        maxOppScore: 0,
        minOppScore: 0,
        numOpponents: 0,
      };

      if (data.leaderboard.length > 1) {
        // Only try to retrieve prize if there are opponents
        prize = ruleset.getRewardsByRank(currentRank) as {
          type?: string;
          subType?: string;
          amount?: number;
        };

        const { accOppScore, maxOppScore, minOppScore } = data.leaderboard
          .filter((x) => x.id !== state.id)
          .reduce(
            (prev, x) => ({
              accOppScore: prev.accOppScore + x.score,
              maxOppScore: Math.max(prev.maxOppScore, x.score),
              minOppScore: Math.min(prev.minOppScore, x.score),
            }),
            { accOppScore: 0, maxOppScore: -Infinity, minOppScore: Infinity },
          );

        oppAnalytics.avgOppScore = accOppScore / (data.leaderboard.length - 1);
        oppAnalytics.maxOppScore = maxOppScore;
        oppAnalytics.minOppScore = minOppScore;
        oppAnalytics.numOpponents = data.leaderboard.length - 1;
      }

      const score = data.leaderboard.find((x) => x.id === state.id)?.score ?? 0;

      // Send all analytics events
      sendAnalyticsEvents(api, [
        {
          eventType: 'StarTournamentEnd',
          eventProperties: {
            timeLeft: getters.getRemainingTime(state, api.date.now()),
            position: currentRank,
            rewardName: prize?.subType ?? prize?.type ?? 'none',
            rewardAmount: prize?.amount ?? 0,
            matchMakingCriteria: matchMakingCriteria ?? 'none',
            tournamentType: period.type,
            tournamentId: period.eventId,
            score,
            playerToAvgScore: oppAnalytics.avgOppScore - score,
            playerToMaxScore: oppAnalytics.maxOppScore - score,
            ...oppAnalytics,
          },
        },
      ]);

      return data;
    },
  ),

  /**
   * Claim all unclaimed tournament rewards
   */
  claimMatch2TournamentRewards: action((state, args: void, api) => {
    const rewards = getters.getUnclaimedRewards(state, api.date.now());

    if (rewards.length) {
      // Grant rewards
      grantMatch2Rewards(
        state,
        rewards,
        {
          feature: Feature.MATCH2.TOURNAMENT._,
          $subFeature: Feature.MATCH2.TOURNAMENT.COLLECT,
        },
        api,
      );

      // List of analytcs events to be sent
      const analyticsEvents: AnalyticsEvent[] = [];

      // Add a GrantMatch2TournamentReward analytics event for each reward
      for (const reward of rewards) {
        analyticsEvents.push({
          userId: state.id,
          eventType: 'GrantMatch2TournamentReward',
          eventProperties: { ...reward },
        });
      }

      // Send all analytics events
      if (analyticsEvents.length) {
        sendAnalyticsEvents(api, analyticsEvents);
      }
    }
    // set all unclaimed rewards to claimed
    const unclaimedEvents = getters.getUnclaimedEvents(state);
    unclaimedEvents.forEach((event) => {
      modifiers.setEventClaimed(state, event.id, api.date.now());
    });

    return rewards.length > 0;
  }),

  /**
   * Claim all unclaimed tournament rewards
   */
  match2CleanupOldTournaments: action((state, args: void, api) => {
    const now = api.date.now();
    const events = state.match2.tournament.events;
    Object.keys(events).forEach((eventId) => {
      const eventTime = new Date(eventId).getTime();
      if (eventTime < now - duration({ days: maxTournamentsAgeDays })) {
        delete events[eventId];
      }
    });
  }),
});
